import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useQuickBillContext } from "../hooks/useQuickBill";

import BoletoItem from "../Item";
import Loading from "components/layout/loading";
import ModalSelectUnit from "../ModalSelectUnit";
import Pagination from "components/layout/Pagination";
import QuestionPool from "../QuestionPool";
import TextField from "components/layout/form/textField";
import TextSelect from "components/layout/form/TextSelect";
import { useGaEvents } from "../hooks/useGaEvents";

import {
  BillsContentWrap,
  Body,
  BoxFilters,
  BoxPaddingContainer,
  Container,
  Content,
  Divider,
  FilterStatus,
  FilterStatusWrapper,
  FilterUnit,
  FilterWrap,
  Header,
  HeaderInfos,
  HeaderSubtitle,
  HeaderTitle,
} from "./styles";

const BillsContainer: React.FC = () => {
  const [openModalQuestions, setOpenModalQuestions] = useState(false);
  const [openModalSelectedUnit, setOpenModalSelectedUnit] = useState(false);
  const [showItems, setShowItems] = useState(false);
  const [filterOption, setFilterOption] = useState("condominio");
  const [filterValue, setFilterValue] = useState("");

  const {
    handleClickFilterAll,
    handleClickFilterClosed,
    handleClickFilterOpen,
    handleClickFilterOrderBy,
    handleClickFilterPaid,
    handleClickNavigatePage,
    errorRequestBills,
  } = useGaEvents();

  const {
    document,
    bills,
    handleCustomFilter,
    customFilter,
    status,
    setStatus,
    totalPages,
    currentPage,
    setCurrentPage,
    loading,
    poolToken,
    selectedUnit,
    units,
    hasOneUnit,
    billError,
    setCondominiumName,
    setUnitName,
  } = useQuickBillContext();

  const billsData = useMemo(() => {
    return bills?.map((item) => {
      const newValue = {
        invoice: item,
        installment: { paymentTypeBoleto: true, ...item },
      };

      return newValue;
    });
  }, [bills]);

  useEffect(() => {
    if (poolToken) {
      setShowItems(true);
    } else {
      setOpenModalQuestions(true);
    }
  }, [poolToken]);

  useEffect(() => {
    if (!selectedUnit?.instanceId && units.length > 0 && !hasOneUnit) {
      setOpenModalSelectedUnit(true);
    }
  }, [selectedUnit, hasOneUnit, units]);

  useEffect(() => {
    if (document && selectedUnit.instanceId) {
      errorRequestBills(document, selectedUnit);
    }
  }, [billError]);

  const handleFilterValue = useCallback(
    (value: string) => {
      setFilterValue(value);
      if (!value) {
        setCondominiumName("");
        setUnitName("");
      }
      if (value.length < 2) return;
      if (filterOption === "condominio") {
        setUnitName("");
        setCondominiumName(value);
      } else {
        setCondominiumName("");
        setUnitName(value);
      }
    },
    [filterOption, filterValue]
  );

  return (
    <Container>
      <Header>
        <HeaderInfos>
          <HeaderTitle>Histórico de boletos</HeaderTitle>
          <HeaderSubtitle>
            Aqui você encontra todos os seus boletos!
          </HeaderSubtitle>
          <HeaderSubtitle>
            ⚠️ Fique atento! Pagamentos após o vencimento geram multa e juros. O
            fornecimento é suspenso automaticamente após 20 dias de atraso.
          </HeaderSubtitle>
        </HeaderInfos>
      </Header>
      <Body>
        <FilterWrap>
          <BoxPaddingContainer>
            <FilterUnit>
              <TextSelect
                label="Filtrar por"
                name="selectedFilter"
                type="text"
                value={filterOption}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value ?? "";
                  setFilterOption(value ?? "");
                  setFilterValue("");
                }}
              >
                <option key="condominio" value="condominio">
                  Condomínio
                </option>
                <option key="unidade" value="unidade">
                  Unidade
                </option>
              </TextSelect>
              <TextField
                label={`Insira ${
                  filterOption === "condominio" ? "o condomínio" : "a unidade"
                }`}
                placeholder={`Insira ${
                  filterOption === "condominio" ? "o condomínio" : "a unidade"
                }`}
                value={filterValue}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value ?? "";
                  handleFilterValue(value);
                }}
              />
            </FilterUnit>
            <BoxFilters>
              <TextSelect
                label="Ordenar por"
                name="selectedPeriod"
                type="text"
                value={customFilter}
                onChange={(e) => {
                  handleClickFilterOrderBy(
                    document,
                    selectedUnit,
                    // @ts-ignore
                    e?.target?.value ?? ""
                  );
                  // @ts-ignore
                  handleCustomFilter(e);
                }}
              >
                <option key="recentToOld" value="recentToOld">
                  Mais recente para o mais antigo
                </option>
                <option key="oldToRecent" value="oldToRecent">
                  Mais antigo para o mais recente
                </option>
                <option key="old15days" value="old15days">
                  Últimos 15 dias
                </option>
                <option key="old30days" value="old30days">
                  Últimos 30 dias
                </option>
                <option key="old60days" value="old60days">
                  Últimos 60 dias
                </option>
                <option key="old90days" value="old90days">
                  Últimos 90 dias
                </option>
              </TextSelect>
              <FilterStatusWrapper>
                <FilterStatus
                  active={status === "A_VENCER"}
                  onClick={() => {
                    handleClickFilterOpen(document, selectedUnit);
                    setStatus("A_VENCER");
                  }}
                >
                  A vencer
                </FilterStatus>
                <FilterStatus
                  active={status === "VENCIDO"}
                  onClick={() => {
                    handleClickFilterClosed(document, selectedUnit);
                    setStatus("VENCIDO");
                  }}
                >
                  Vencidos
                </FilterStatus>
                <FilterStatus
                  active={status === "PAGO"}
                  onClick={() => {
                    handleClickFilterPaid(document, selectedUnit);
                    setStatus("PAGO");
                  }}
                >
                  Pagos
                </FilterStatus>
                <FilterStatus
                  active={status === "TODOS"}
                  onClick={() => {
                    handleClickFilterAll(document, selectedUnit);
                    setStatus("TODOS");
                  }}
                >
                  Todos
                </FilterStatus>
              </FilterStatusWrapper>
            </BoxFilters>
          </BoxPaddingContainer>
        </FilterWrap>
        <BillsContentWrap>
          <Content>
            {loading && <Loading />}
            {!loading &&
              showItems &&
              billsData?.map((bill, index) => (
                <React.Fragment key={bill.installment.id}>
                  {index !== 0 && <Divider />}
                  <BoletoItem
                    key={bill.installment.emissionDate}
                    installment={bill.installment}
                    invoice={bill.invoice}
                  />
                </React.Fragment>
              ))}
            {!loading && !showItems && (
              <p>
                Você precisa responder corretamente as questões para visualizar
                os seus boletos!
              </p>
            )}
            {!loading && showItems && billsData?.length === 0 && (
              <p>Não há boletos disponíveis no momento!</p>
            )}
            {totalPages > 1 && (
              <Pagination
                totalPages={totalPages}
                selectedPage={currentPage}
                onPageChange={setCurrentPage}
                isLoading={loading}
                onGaClickPagination={(page) => {
                  handleClickNavigatePage(document, selectedUnit, page);
                }}
              />
            )}
          </Content>
        </BillsContentWrap>
      </Body>
      {!openModalSelectedUnit && openModalQuestions && (
        <QuestionPool body={{ document }} handleModal={setOpenModalQuestions} />
      )}
      {openModalSelectedUnit && units.length > 0 && (
        <ModalSelectUnit handleModal={setOpenModalSelectedUnit} />
      )}
    </Container>
  );
};

export default BillsContainer;
